/**
 * Helper centralisé de push dataLayer GA4 pour Toolouer.
 *
 * Règles à respecter, gravées dans le marbre (cf. plan_taggage.html, Digitalix) :
 *   - reset `ecommerce: null` avant chaque push (SPA Next.js : la persistance du dataLayer
 *     contamine les events suivants si on ne reset pas)
 *   - tous les montants en HT, jamais TTC. Le toggle Pro/Particulier n'affecte que
 *     l'affichage, pas le dataLayer.
 *   - pour les locations : price = unitPriceHt × rentalDays (toujours utiliser le
 *     rentalDays fourni, ne jamais le redériver depuis les dates)
 *   - sellingType "RENT"/"NEW" est traduit en "location"/"achat" pour GA4
 *   - les champs rental_* ne sont présents que pour les items "location"
 */

import type { CartItem } from "./cart-context";

type GA4SellingType = "location" | "achat";

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  selling_type: GA4SellingType;
  price: number;
  quantity: number;
  index?: number;
  daily_rate?: number;
  rental_days?: number;
  rental_start_date?: string;
  rental_end_date?: string;
}

/**
 * Représentation minimale d'un produit pour les events qui n'utilisent pas le panier
 * (view_item_list, select_item, view_item). Ne contient que les champs HT — pas le
 * toggle Pro/Particulier.
 */
export interface ProductForGA4 {
  id: number | string;
  title: string;
  category_name?: string | null;
  selling_type: "RENT" | "NEW" | string | null;
  unit_price_ht: number;
}

/**
 * Item retourné par /api/orders/confirmation, utilisé pour purchase.
 */
export interface ConfirmationItem {
  product_id: number;
  product_title: string;
  category_name?: string | null;
  selling_type: "RENT" | "NEW" | string | null;
  quantity: number;
  unit_price_ht: number | string;
  rental_days?: number | null;
  rental_start_date?: string | null;
  rental_end_date?: string | null;
}

function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function mapSellingType(t: string | null | undefined): GA4SellingType {
  return t === "RENT" ? "location" : "achat";
}

/**
 * Construit un item GA4 depuis un CartItem (panier client).
 * Utilisé pour add_to_cart, begin_checkout, add_shipping_info, add_payment_info.
 */
export function mapCartItemToGA4Item(item: CartItem, index?: number): GA4Item {
  const isRent = item.sellingType === "RENT";
  const rentalDays = item.rentalDays || 0;
  const dailyRate = item.unitPriceHt;
  const price = isRent ? round2(dailyRate * rentalDays) : dailyRate;

  const ga4: GA4Item = {
    item_id: String(item.productId),
    item_name: item.productTitle,
    selling_type: mapSellingType(item.sellingType),
    price,
    quantity: item.quantity,
  };

  if (item.categoryName) ga4.item_category = item.categoryName;
  if (typeof index === "number") ga4.index = index + 1;

  if (isRent) {
    ga4.daily_rate = round2(dailyRate);
    if (rentalDays > 0) ga4.rental_days = rentalDays;
    if (item.rentalStartDate) ga4.rental_start_date = item.rentalStartDate;
    if (item.rentalEndDate) ga4.rental_end_date = item.rentalEndDate;
  }

  return ga4;
}

/**
 * Construit un item GA4 depuis un produit (catalogue / fiche produit), sans contexte panier.
 * Utilisé pour view_item_list, select_item, view_item.
 *
 * En liste / fiche, aucune date n'est encore sélectionnée pour les locations : on envoie
 * le tarif/jour comme `price` indicatif, sans paramètres rental_*.
 */
export function mapProductToGA4Item(product: ProductForGA4, index?: number): GA4Item {
  const ga4: GA4Item = {
    item_id: String(product.id),
    item_name: product.title,
    selling_type: mapSellingType(product.selling_type),
    price: round2(toNumber(product.unit_price_ht)),
    quantity: 1,
  };
  if (product.category_name) ga4.item_category = product.category_name;
  if (typeof index === "number") ga4.index = index + 1;
  return ga4;
}

/**
 * Construit un item GA4 depuis un item de commande confirmée (API /api/orders/confirmation).
 * Utilisé pour purchase.
 */
export function mapConfirmationItemToGA4Item(item: ConfirmationItem, index?: number): GA4Item {
  const isRent = item.selling_type === "RENT";
  const dailyRate = toNumber(item.unit_price_ht);
  const rentalDays = item.rental_days || 0;
  const price = isRent ? round2(dailyRate * rentalDays) : dailyRate;

  const ga4: GA4Item = {
    item_id: String(item.product_id),
    item_name: item.product_title,
    selling_type: mapSellingType(item.selling_type),
    price,
    quantity: item.quantity,
  };

  if (item.category_name) ga4.item_category = item.category_name;
  if (typeof index === "number") ga4.index = index + 1;

  if (isRent) {
    ga4.daily_rate = round2(dailyRate);
    if (rentalDays > 0) ga4.rental_days = rentalDays;
    if (item.rental_start_date) ga4.rental_start_date = item.rental_start_date;
    if (item.rental_end_date) ga4.rental_end_date = item.rental_end_date;
  }

  return ga4;
}

interface DataLayerPushPayload {
  event?: string;
  ecommerce?: Record<string, unknown> | null;
  [key: string]: unknown;
}

/**
 * Push avec reset `ecommerce: null` obligatoire avant l'event en SPA.
 * No-op côté serveur (Next.js SSR/SSG).
 */
export function pushDataLayer(event: string, ecommerce: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: DataLayerPushPayload[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ ecommerce: null });
  w.dataLayer.push({ event, ecommerce });
}

/**
 * Calcule le `value` pour un événement panier (begin_checkout, add_shipping_info,
 * add_payment_info, purchase).
 *
 *   value = somme(items[].price × items[].quantity) + shipping + insurance_amount
 *
 * La caution (deposit_amount) n'entre PAS dans value (garantie restituée, pas du CA).
 */
export function computeValue(
  items: GA4Item[],
  shipping = 0,
  insuranceAmount = 0
): number {
  const itemsTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  return round2(itemsTotal + shipping + insuranceAmount);
}

/**
 * Bloc user_data pour Enhanced Conversions (add_shipping_info, add_payment_info, purchase).
 * Email/téléphone/nom en clair normalisés — GTM se charge du hash côté tag.
 *
 *   email → trim + lowercase
 *   phone → E.164 (préfixe +33 par défaut pour les numéros FR)
 *   address.country → "FR" par défaut
 */
export interface UserDataInput {
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  region?: string | null;
  country?: string | null;
}

export interface UserData {
  email?: string;
  phone_number?: string;
  address?: {
    first_name?: string;
    last_name?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

function normalizeEmail(v: string | null | undefined): string | undefined {
  if (!v) return undefined;
  const s = v.trim().toLowerCase();
  return s || undefined;
}

/**
 * Phone → E.164. Pour la France :
 *   "0612345678" → "+33612345678"
 *   "06 12 34 56 78" → "+33612345678"
 *   "+33612345678" → "+33612345678" (inchangé)
 *   "+44..." → conservé tel quel
 */
function normalizePhoneE164(v: string | null | undefined, defaultCountry = "FR"): string | undefined {
  if (!v) return undefined;
  const cleaned = v.replace(/[\s.\-()]/g, "");
  if (!cleaned) return undefined;
  if (cleaned.startsWith("+")) return cleaned;
  if (defaultCountry === "FR") {
    if (cleaned.startsWith("00")) return "+" + cleaned.slice(2);
    if (cleaned.startsWith("0") && cleaned.length === 10) return "+33" + cleaned.slice(1);
    if (cleaned.length === 9) return "+33" + cleaned; // sans 0 initial
  }
  return cleaned.startsWith("00") ? "+" + cleaned.slice(2) : cleaned;
}

function trim(v: string | null | undefined): string | undefined {
  if (!v) return undefined;
  const s = v.trim();
  return s || undefined;
}

export function buildUserData(input: UserDataInput): UserData | undefined {
  const email = normalizeEmail(input.email);
  const phone = normalizePhoneE164(input.phone);
  const first_name = trim(input.first_name);
  const last_name = trim(input.last_name);
  const street = trim(input.street);
  const city = trim(input.city);
  const region = trim(input.region);
  const postal_code = trim(input.postal_code);
  const country = trim(input.country) || (postal_code || city ? "FR" : undefined);

  const address: NonNullable<UserData["address"]> = {};
  if (first_name) address.first_name = first_name;
  if (last_name) address.last_name = last_name;
  if (street) address.street = street;
  if (city) address.city = city;
  if (region) address.region = region;
  if (postal_code) address.postal_code = postal_code;
  if (country) address.country = country;

  const result: UserData = {};
  if (email) result.email = email;
  if (phone) result.phone_number = phone;
  if (Object.keys(address).length > 0) result.address = address;

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Push avec user_data au niveau racine (Enhanced Conversions).
 * Garde la même garantie de reset ecommerce: null avant le push.
 */
export function pushDataLayerWithUser(
  event: string,
  ecommerce: Record<string, unknown>,
  userData: UserData | undefined
): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { dataLayer?: DataLayerPushPayload[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ ecommerce: null });
  const payload: DataLayerPushPayload = { event, ecommerce };
  if (userData) payload.user_data = userData;
  w.dataLayer.push(payload);
}
